import React, { Dispatch, SetStateAction, useState } from "react";
import BasicTabs from "../../common/BasicTabs/BasicTabs";
import greenIcon from "../../assets/svgs/green-icon.svg";
import redIcon from "../../assets/svgs/red-icon.svg";
import yellowIcon from "../../assets/svgs/yellow-icon.svg";
import Tables from "../Tables/Tables";
import { Box, Typography } from "@mui/material";
import { MyTasksTableStyles } from "./MyTasksTableStyles";
import {
  HIGH_PRIORITY_TASKS,
  LOW_PRIORITY_TASKS,
  MEDIUM_PRIORITY_TASKS,
  TOTAL_TASKS,
} from "../../constants";
import { IAgentTask } from "../../models/agent/policiesListing";
import moment from "moment";
import ModalComponent from "../../common/ModalComponent/ModalComponent";
import MyTasksCalendarCard from "../MyTasksCalendarCard/MyTasksCalendarCard";
import { MyTasksCalendarStyles } from "../MyTasksCalendar/MyTasksCalendarStyles";
interface ClientDetailsProps {
  clientName: string;
  clientEmail: string;
  clientPhone: string;
}
export interface TasksDataProps {
  timeStamp: {
    date: string;
    time: string;
  };
  task: string;
  clientDetails: ClientDetailsProps;
  policy: string;
  priority: string;
  status: string;
}
interface TasksTableDataProps {
  tasksData: IAgentTask[];
  tasksTableHeadingsData: string[];
  activeTab: string;
  selectedKey?: string;
  isEditTask: boolean;
  setOpenTaskDialog: Dispatch<SetStateAction<boolean>>;
  setModalData: Dispatch<SetStateAction<IAgentTask | null>>;
}

const formatDateAndTime = (date: string, time: string) => {
  if (!date?.trim() || !time?.trim) return;
  if (time === "") {
    const date1 = date?.includes("T") ? date?.split("T")[0] : date;
    return moment(date1).format("DD MMM YYYY");
  } else {
    const date1 = date?.includes("T") ? date?.split("T")[0] : date;
    const dateTime = new Date(`${date1}T${time}`);

    return moment(dateTime).format("DD MMM YYYY,hh:mm a");
  }
};

export const transformTasksData = (data: IAgentTask[]) => {
  return data.map((task) => ({
    id: task.id,
    timeStamp: formatDateAndTime(task?.dueDate, task?.dueTime),
    taskName: task.taskName,
    customerName: task.customerName,
    policyNumber: task.policyNumber,
    description: task.description,
    priority: task?.priority ? task.priority.toLowerCase() : "unknown",
    status: task.status,
    dueTime: task.dueTime,
    dueDate: task.dueDate,
    system: task.system,
    carrier: task.carrier,
    policyId: task.policyId,
    lobCode: task.lobCode,
    providerId: task.providerId,
  }));
};

const MyTasksTable: React.FC<TasksTableDataProps> = ({
  tasksData,
  tasksTableHeadingsData,
  activeTab,
  selectedKey,
  isEditTask,
  setOpenTaskDialog,
  setModalData,
}) => {
  // Filter data based on status first
  const getFilteredTasksByStatus = (tasks: IAgentTask[]) => {
    if (!selectedKey || selectedKey === "0") return tasks;

    const statusMap: { [key: string]: string } = {
      "1": "Open",
      "2": "Completed",
    };

    if (selectedKey === "3") {
      const currentDateTime = new Date();
      return tasks.filter((task) => {
        if (!task.dueDate) return false;
      
        const isOpen = task.status === "Open";
        
        const taskDateTime = new Date(`${task.dueDate}T${task.dueTime || "00:00:00"}`);
        
        return isOpen && taskDateTime < currentDateTime;
      });
    }

    return tasks.filter((task) => task.status === statusMap[selectedKey]);
  };

  const filteredTasksByStatus = getFilteredTasksByStatus(tasksData);

  // Filter data based on priority for each tab
  const highPriorityTasks = transformTasksData(
    filteredTasksByStatus.filter(
      (task) => task.priority?.toLowerCase() === "high" || !task.priority
    )
  );

  const mediumPriorityTasks = transformTasksData(
    filteredTasksByStatus.filter(
      (task) => task.priority?.toLowerCase() === "medium"
    )
  );

  const lowPriorityTasks = transformTasksData(
    filteredTasksByStatus.filter(
      (task) => task.priority?.toLowerCase() === "low"
    )
  );

  const allTasks = transformTasksData(filteredTasksByStatus);

  // Tab data
  const tabData = [
    {
      label: TOTAL_TASKS,
      content: (
        <Tables
          headerData={tasksTableHeadingsData}
          type="myTasksTable"
          data={allTasks}
          selectedKey={selectedKey}
          setOpenTaskDialog={setOpenTaskDialog}
          setModalData={setModalData}
          isEditTask={isEditTask}
        />
      ),
      secondaryLabel: allTasks.length.toString(),
    },
    {
      label: HIGH_PRIORITY_TASKS,
      content: (
        <Tables
          headerData={tasksTableHeadingsData}
          type="myTasksTable"
          data={highPriorityTasks}
          selectedKey={selectedKey}
          setOpenTaskDialog={setOpenTaskDialog}
          setModalData={setModalData}
          isEditTask={isEditTask}
        />
      ),
      secondaryLabel: highPriorityTasks.length.toString(),
      imageUrl: redIcon,
      activeImageUrl: redIcon,
    },
    {
      label: MEDIUM_PRIORITY_TASKS,
      content: (
        <Tables
          headerData={tasksTableHeadingsData}
          type="myTasksTable"
          data={mediumPriorityTasks}
          selectedKey={selectedKey}
          setOpenTaskDialog={setOpenTaskDialog}
          setModalData={setModalData}
          isEditTask={isEditTask}
        />
      ),
      secondaryLabel: mediumPriorityTasks.length.toString(),
      imageUrl: yellowIcon,
      activeImageUrl: yellowIcon,
    },
    {
      label: LOW_PRIORITY_TASKS,
      content: (
        <Tables
          headerData={tasksTableHeadingsData}
          type="myTasksTable"
          data={lowPriorityTasks}
          selectedKey={selectedKey}
          setOpenTaskDialog={setOpenTaskDialog}
          setModalData={setModalData}
          isEditTask={isEditTask}
        />
      ),
      secondaryLabel: lowPriorityTasks.length.toString(),
      imageUrl: greenIcon,
      activeImageUrl: greenIcon,
    },
  ];

  return (
    <Box sx={MyTasksTableStyles.container}>
      <BasicTabs tabs={tabData} />
    </Box>
  );
};

export default MyTasksTable;
